import {Action, handleActions} from 'redux-actions';
import {v4 as uuidv4} from 'uuid';

import {curry} from 'ramda';
import {TimelineMarker} from '../../../Timeline/TimelineTypes';
import {
    AD_BREAKS_SET,
    AD_BREAKS_RESET,
    AD_BREAKS_ADD_MARKER,
    AddAdBreakMarkerPayload,
    AD_BREAKS_EDIT,
    EditAdBreaksMarkerPayload,
    AD_BREAKS_DELETE,
} from './AdBreaksActions';

export interface AdBreaksState {
    markers: TimelineMarker[];
    rowId: string;
}

type AdBreakMarkerAction = Action<AddAdBreakMarkerPayload>;
type EditAdBreaksMarkerAction = Action<EditAdBreaksMarkerPayload>
type DeleteAdBreaksMarkerAction = Action<string>;

export const rowId = uuidv4();

export const ascendingCompare = (markerA: TimelineMarker, markerB: TimelineMarker) => {
    return markerA.start < markerB.start ? -1 : 1;
};

export const setAdBreaks = (state: AdBreaksState, {payload}: Action<TimelineMarker[]>): AdBreaksState => {
    const markers = payload;
    
    if (!markers)
        return state;
    
    const sortedMarkers = markers.sort(ascendingCompare);
    
    return {
        ...state,
        markers: sortedMarkers,
    };
};

export const resetAdBreaks = (): AdBreaksState => {
    return initialState;
};

export const addAdBreakMarker = (state: AdBreaksState, action: AdBreakMarkerAction): AdBreaksState => {
    const marker = action.payload?.marker;
    
    if (!marker)
        return state;
    
    const markers = Array.from(state.markers);
    
    markers.push(marker);
    const sortedMarkers = markers.sort(ascendingCompare);
    
    return {
        ...state,
        markers: sortedMarkers,
    };
};

export const sameMarker = curry((markerId: string, marker: TimelineMarker) => markerId === marker.identifier);
export const notSameMarker = curry((markerId: string, marker: TimelineMarker) => markerId !== marker.identifier);

export const replaceMarker = curry((updatedMarker: TimelineMarker, marker: TimelineMarker) => {
    if (sameMarker(updatedMarker.identifier, marker))
        return updatedMarker;
    
    return marker;
});

export const editMarker = (state: AdBreaksState, action: EditAdBreaksMarkerAction): AdBreaksState => {
    const {marker} = action.payload;
    const {identifier} = marker;
    const {markers} = state;
    
    const handleSameMarker = sameMarker(identifier);
    const originalMarker = markers.find(handleSameMarker);
    
    if (!originalMarker) {
        return state;
    }
    
    const handleReplaceMarker = replaceMarker(marker);
    const updatedMarkers = markers.map(handleReplaceMarker);
    const sortedMarkers = updatedMarkers.sort(ascendingCompare);
    
    return {
        ...state,
        markers: sortedMarkers,
    };
};

export const deleteMarker = (state: AdBreaksState, action: DeleteAdBreaksMarkerAction): AdBreaksState => {
    const markerId = action.payload;
    const {markers} = state;
    
    const removeMarker = notSameMarker(markerId);
    const updatedMarkers = markers.filter(removeMarker);
    
    return {
        ...state,
        markers: updatedMarkers,
    };
};

export const initialState: AdBreaksState = {
    markers: [],
    rowId,
};

const handlers = {
    [AD_BREAKS_SET]: setAdBreaks,
    [AD_BREAKS_RESET]: resetAdBreaks,
    [AD_BREAKS_ADD_MARKER]: addAdBreakMarker,
    [AD_BREAKS_EDIT]: editMarker,
    [AD_BREAKS_DELETE]: deleteMarker,
};

export default handleActions<AdBreaksState, any>(handlers, initialState);
